import { getSceneNodeChildren } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";

export type LayerDropTargetParentType = "page" | "frame" | "group" | "boolean" | "component" | "componentVariant" | "instance";

export interface LayerDropTarget {
  parentId: string;
  parentType: LayerDropTargetParentType;
  index: number;
}

export interface MaskDecoration {
  maskedBySibling: boolean;
  maskAffectsSiblings: boolean;
  railDepth: number;
}

export interface FlatLayerNodeRow {
  kind: "node";
  node: SceneNode;
  depth: number;
  index: number;
  parentId: string;
  parentType: LayerDropTargetParentType;
  maskDecoration?: MaskDecoration;
}

export interface FlatLayerEmptyDropRow {
  kind: "empty-drop";
  depth: number;
  parentId: string;
  parentType: LayerDropTargetParentType;
}

export type FlatLayerRow = FlatLayerNodeRow | FlatLayerEmptyDropRow;

export const LAYER_ROW_HEIGHT_PX = 32;
export const LAYER_EMPTY_DROP_ROW_HEIGHT_PX = 20;

export function layersForDisplay(children: SceneNode[]): Array<{ node: SceneNode; index: number }> {
  return children.map((node, index) => ({ node, index })).reverse();
}

export function buildFlatLayerRows(
  children: SceneNode[],
  parentId: string,
  parentType: LayerDropTargetParentType,
  collapsedNodeIds: Set<string>,
  depth = 0,
  inheritedMask?: MaskDecoration,
): FlatLayerRow[] {
  const rows: FlatLayerRow[] = [];
  const maskByChild = maskDecorationsForChildren(children, depth);

  for (const { node, index } of layersForDisplay(children)) {
    const localMask = maskByChild.get(node.id);
    const maskDecoration = localMask?.maskedBySibling || localMask?.maskAffectsSiblings
      ? localMask
      : inheritedMask ?? localMask;

    rows.push({
      kind: "node",
      node,
      depth,
      index,
      parentId,
      parentType,
      maskDecoration,
    });

    const nodeChildren = getSceneNodeChildren(node);
    if (!nodeChildren || collapsedNodeIds.has(node.id)) {
      continue;
    }

    const childInherited = maskDecoration?.maskedBySibling
      ? { maskedBySibling: true, maskAffectsSiblings: false, railDepth: maskDecoration.railDepth }
      : undefined;
    rows.push(...buildFlatLayerRows(nodeChildren, node.id, node.type as LayerDropTargetParentType, collapsedNodeIds, depth + 1, childInherited));

    if (nodeChildren.length === 0) {
      rows.push({
        kind: "empty-drop",
        depth: depth + 1,
        parentId: node.id,
        parentType: node.type as LayerDropTargetParentType,
      });
    }
  }

  return rows;
}

export function flattenedVisibleLayerNodeIds(children: SceneNode[], collapsedNodeIds: Set<string>): string[] {
  return buildFlatLayerRows(children, "", "page", collapsedNodeIds)
    .filter((row): row is FlatLayerNodeRow => row.kind === "node")
    .map(row => row.node.id);
}

export function flatRowHeight(row: FlatLayerRow): number {
  return row.kind === "empty-drop" ? LAYER_EMPTY_DROP_ROW_HEIGHT_PX : LAYER_ROW_HEIGHT_PX;
}

export function buildFlatRowOffsets(rows: FlatLayerRow[]): number[] {
  const offsets = [0];
  let total = 0;
  for (const row of rows) {
    total += flatRowHeight(row);
    offsets.push(total);
  }
  return offsets;
}

export function findFlatRowIndexAtOffset(offsets: number[], offset: number): number {
  let low = 0;
  let high = offsets.length - 2;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const rowTop = offsets[mid]!;
    const rowBottom = offsets[mid + 1]!;
    if (offset < rowTop) {
      high = mid - 1;
    } else if (offset >= rowBottom) {
      low = mid + 1;
    } else {
      return mid;
    }
  }
  return Math.max(0, Math.min(low, offsets.length - 2));
}

export function flatRowOffsetTop(offsets: number[], index: number): number {
  return offsets[index] ?? 0;
}

function maskDecorationsForChildren(children: SceneNode[], railDepth: number): Map<string, MaskDecoration> {
  const decorations = new Map<string, MaskDecoration>();
  let activeMaskIndex: number | undefined;

  children.forEach((node, index) => {
    if (node.isMask) {
      activeMaskIndex = index;
      const nextMaskIndex = children.findIndex((candidate, candidateIndex) => candidateIndex > index && candidate.isMask);
      const segmentEnd = nextMaskIndex === -1 ? children.length : nextMaskIndex;
      decorations.set(node.id, {
        maskedBySibling: false,
        maskAffectsSiblings: children.slice(index + 1, segmentEnd).some(candidate => !candidate.isMask),
        railDepth,
      });
      return;
    }

    decorations.set(node.id, {
      maskedBySibling: activeMaskIndex !== undefined,
      maskAffectsSiblings: false,
      railDepth,
    });
  });

  return decorations;
}

export function layerDisplayName(node: SceneNode): string {
  if (node.type === "text" && !node.nameManuallyEdited && isAutoTextLayerName(node.name)) {
    const contentName = node.text.characters.replace(/\s+/g, " ").trim();
    if (contentName.length > 0) {
      return contentName;
    }
  }

  return node.name || (node.type === "boolean" ? titleCase(node.operation) : "Layer");
}

function isAutoTextLayerName(name: string): boolean {
  return /^Text(?: \d+)?$/.test(name.trim());
}

function titleCase(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}
